import createClient, { type Middleware } from 'openapi-fetch';
import { t } from '@/i18n';
import type { components, paths } from './schema';

export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

/** Reads a single cookie value from `document.cookie` by name. */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Mirrors the `XSRF-TOKEN` cookie into the `X-CSRF-Token` request header on
 * every state-changing request (POST / PUT / PATCH / DELETE).
 * The server performs a constant-time comparison of both values.
 */
const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method)) {
      const token = readCookie('XSRF-TOKEN');
      if (token) request.headers.set('X-CSRF-Token', token);
    }
    return request;
  },
};

/**
 * Dispatches a DOM event on every 401 so that `AuthContext` can react
 * (clear the authenticated state) without coupling this module to React.
 */
const unauthorizedMiddleware: Middleware = {
  onResponse({ response }) {
    if (response.status === 401) {
      window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
    }
    return response;
  },
};

export const api = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  credentials: 'include',
});
api.use(csrfMiddleware);
api.use(unauthorizedMiddleware);

export type ApiErrorEnvelope = components['schemas']['ErrorEnvelope'];
export type ApiErrorShape = ApiErrorEnvelope['error'];

/**
 * Narrow value produced by openapi-fetch's `error` field (or any thrown/returned
 * value) into a best-effort `ApiErrorShape`. Falls back to a synthetic envelope
 * when the server response doesn't match the contract (network error, HTML 5xx).
 */
export function toApiError(err: unknown, fallbackStatus = 0): ApiErrorShape {
  if (err && typeof err === 'object') {
    const maybeEnvelope = err as Partial<ApiErrorEnvelope> & Partial<ApiErrorShape>;
    if (
      maybeEnvelope.error &&
      typeof maybeEnvelope.error === 'object' &&
      typeof maybeEnvelope.error.message === 'string'
    ) {
      return maybeEnvelope.error;
    }
    if (
      typeof maybeEnvelope.code === 'string' &&
      typeof maybeEnvelope.message === 'string' &&
      typeof maybeEnvelope.status === 'number'
    ) {
      return maybeEnvelope as ApiErrorShape;
    }
    if (err instanceof Error) {
      return { code: 'NETWORK_ERROR', message: err.message, status: fallbackStatus };
    }
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: t.api.unknownError,
    status: fallbackStatus,
  };
}
